import React, { useState, useEffect } from 'react';
import * as M from '@material-ui/core';
import { Add, Delete, Edit, Search, Clear } from '@material-ui/icons';
import { api } from '@services/api';
import { ToastMessage } from '@components/ToastMessage';
import { type Color } from '@material-ui/lab';
import * as S from './styles';

interface Exame {
  id_exames: string;
  nome: string;
}

export function Exames() {
  const [exames, setExames] = useState<Exame[]>([]);
  const [examesFiltrados, setExamesFiltrados] = useState<Exame[]>([]);
  const [codExame, setCodExame] = useState('');
  const [nomeExame, setNomeExame] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [messageSend, setMessageSend] = useState({
    cod: '',
    message: '',
    severity: '' as Color,
    show: false
  });

  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Estados para o modal de exclusão
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [exameToDelete, setExameToDelete] = useState<Exame | null>(null);

  // Estados para o modal de edição
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [exameToEdit, setExameToEdit] = useState<Exame | null>(null);
  const [editCodExame, setEditCodExame] = useState('');
  const [editNomeExame, setEditNomeExame] = useState('');

  useEffect(() => {
    fetchExames();
  }, []);

  // 🔥 FUNÇÃO PARA FILTRAR EXAMES
  const filtrarExames = (termo: string) => {
    if (!termo.trim()) {
      setExamesFiltrados(exames);
      setPage(0);
      return;
    }

    const termoLowerCase = termo.toLowerCase().trim();
    const filtrados = exames.filter(
      (exame) =>
        exame.id_exames.toLowerCase().includes(termoLowerCase) ||
        exame.nome.toLowerCase().includes(termoLowerCase)
    );
    setExamesFiltrados(filtrados);
    setPage(0);
  };

  const handleSearch = () => {
    filtrarExames(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setExamesFiltrados(exames);
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  async function fetchExames() {
    setIsLoading(true);
    try {
      const res = await api.get('/ravim/exames');
      if (res.status === 200) {
        setExames(res.data);
        setExamesFiltrados(res.data);
      }
    } catch (error: any) {
      setMessageSend({
        cod: error.code,
        message: error.message,
        severity: 'error',
        show: true
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function addExame() {
    const data = {
      id_exame: codExame,
      nome_exame: nomeExame
    };

    setIsLoading(true);
    try {
      const res = await api.post('/ravim/cad-exames', data);
      if (res.status === 200) {
        setMessageSend({
          cod: String(res.status),
          message: res.data.message,
          severity: 'success',
          show: true
        });
        setCodExame('');
        setNomeExame('');
        fetchExames();
      } else if (res.status === 400) {
        setMessageSend({
          cod: String(res.status),
          message: res.data.message,
          severity: 'warning',
          show: true
        });
      }
    } catch (error: any) {
      setMessageSend({
        cod: error.code,
        message: error.message,
        severity: 'error',
        show: true
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteClick = (exame: Exame) => {
    setExameToDelete(exame);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setExameToDelete(null);
  };

  async function confirmDeleteExame() {
    if (exameToDelete) {
      setIsLoading(true);
      try {
        const res = await api.delete(`/ravim/exames/${exameToDelete.id_exames}`);
        if (res.status === 200) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'success',
            show: true
          });
          fetchExames();
        } else if (res.status === 400) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'warning',
            show: true
          });
        }
      } catch (error: any) {
        setMessageSend({
          cod: error.code,
          message: error.message,
          severity: 'error',
          show: true
        });
      } finally {
        setIsLoading(false);
        handleCloseDeleteDialog();
      }
    }
  }

  const handleEditClick = (exame: Exame) => {
    setExameToEdit(exame);
    setEditCodExame(exame.id_exames);
    setEditNomeExame(exame.nome);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setExameToEdit(null);
    setEditCodExame('');
    setEditNomeExame('');
  };

  async function saveEditedExame() {
    if (exameToEdit) {
      const data = {
        id_exame: editCodExame,
        nome_exame: editNomeExame
      };
      setIsLoading(true);
      try {
        const res = await api.put(`/ravim/exames/${exameToEdit.id_exames}`, data);
        if (res.status === 200) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'success',
            show: true
          });
          fetchExames();
        } else if (res.status === 400) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'warning',
            show: true
          });
        }
      } catch (error: any) {
        setMessageSend({
          cod: error.code,
          message: error.message,
          severity: 'error',
          show: true
        });
      } finally {
        setIsLoading(false);
        handleCloseEditDialog();
      }
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const dadosExibidos = examesFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <M.Container maxWidth="lg" style={{ padding: '20px' }}>
      <M.Box mb={4}>
        <M.Typography
          style={{ textAlign: 'center', fontWeight: 'bold' }}
          variant="h4"
          component="h1"
          color="primary"
        >
          Gerenciamento de Exames
        </M.Typography>
      </M.Box>

      <M.Grid container spacing={3}>
        {/* Seção de Cadastro de Exames */}
        <M.Grid item xs={12} md={4}>
          <M.Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
            <M.Typography variant="h6" gutterBottom>Cadastrar Novo Exame</M.Typography>
            <M.Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              <M.TextField
                onChange={(e) => setCodExame(e.target.value)}
                label="Código"
                variant="outlined"
                value={codExame}
                fullWidth
                size="small"
              />
              <M.TextField
                label="Nome do exame"
                variant="outlined"
                onChange={(e) => setNomeExame(e.target.value)}
                value={nomeExame}
                fullWidth
                size="small"
              />
              {/* 🔥 BOTÃO CORRIGIDO - Usando M.Button do Material-UI */}
              <M.Button
                startIcon={<Add />}
                onClick={addExame}
                disabled={!(codExame.length > 0 && nomeExame.length > 0)}
                variant="contained"
                color="primary"
                fullWidth
              >
                {isLoading ? 'Cadastrando...' : 'Cadastrar'}
              </M.Button>
            </M.Box>
          </M.Paper>
        </M.Grid>

        {/* Seção de Listagem de Exames */}
        <M.Grid item xs={12} md={8}>
          <M.Paper elevation={3} style={{ padding: '20px' }}>
            <M.Typography variant="h6" gutterBottom>Exames Cadastrados</M.Typography>
            
            {/* BARRA DE PESQUISA */}
            <M.Box display="flex" alignItems="center" style={{ gap: 16 }} mb={2}>
              <M.TextField
                label="Pesquisar por código ou nome"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                placeholder="Digite o código ou nome do exame..."
                style={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <M.InputAdornment position="start">
                      <Search />
                    </M.InputAdornment>
                  ),
                }}
              />
              {/* 🔥 BOTÃO BUSCAR CORRIGIDO */}
              <M.Button
                onClick={handleSearch}
                variant="contained"
                color="primary"
                startIcon={<Search />}
              >
                Buscar
              </M.Button>
              {searchTerm && (
                // 🔥 BOTÃO LIMPAR CORRIGIDO
                <M.Button
                  onClick={handleClearSearch}
                  variant="outlined"
                  color="secondary"
                  startIcon={<Clear />}
                >
                  Limpar
                </M.Button>
              )}
            </M.Box>

            {searchTerm && (
              <M.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Encontrado(s) {examesFiltrados.length} resultado(s) para "{searchTerm}"
              </M.Typography>
            )}

            {isLoading ? (
              <M.Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <M.CircularProgress />
              </M.Box>
            ) : examesFiltrados.length === 0 ? (
              <M.Typography>
                {searchTerm ? 'Nenhum exame encontrado com o termo pesquisado.' : 'Nenhum exame cadastrado.'}
              </M.Typography>
            ) : (
              <>
                <M.TableContainer>
                  <M.Table>
                    <M.TableHead>
                      <M.TableRow>
                        <M.TableCell style={{ fontWeight: 'bold' }}>Código</M.TableCell>
                        <M.TableCell style={{ fontWeight: 'bold' }}>Nome do Exame</M.TableCell>
                        <M.TableCell align="right" style={{ fontWeight: 'bold' }}>Ações</M.TableCell>
                      </M.TableRow>
                    </M.TableHead>
                    <M.TableBody>
                      {dadosExibidos.map((exame) => (
                        <M.TableRow key={exame.id_exames} hover>
                          <M.TableCell>
                            {searchTerm ? (
                              <span dangerouslySetInnerHTML={{
                                __html: exame.id_exames.replace(
                                  new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                                  '<mark style="background-color: yellow; padding: 0 2px;">$1</mark>'
                                )
                              }} />
                            ) : (
                              exame.id_exames
                            )}
                          </M.TableCell>
                          <M.TableCell>
                            {searchTerm ? (
                              <span dangerouslySetInnerHTML={{
                                __html: exame.nome.replace(
                                  new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                                  '<mark style="background-color: yellow; padding: 0 2px;">$1</mark>'
                                )
                              }} />
                            ) : (
                              exame.nome
                            )}
                          </M.TableCell>
                          <M.TableCell align="right">
                            <M.IconButton
                              aria-label="editar"
                              onClick={() => handleEditClick(exame)}
                              color="primary"
                              size="small"
                            >
                              <Edit />
                            </M.IconButton>
                            <M.IconButton
                              aria-label="excluir"
                              onClick={() => handleDeleteClick(exame)}
                              color="secondary"
                              size="small"
                            >
                              <Delete />
                            </M.IconButton>
                          </M.TableCell>
                        </M.TableRow>
                      ))}
                    </M.TableBody>
                  </M.Table>
                </M.TableContainer>
                <M.TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={examesFiltrados.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </>
            )}
          </M.Paper>
        </M.Grid>
      </M.Grid>

      {/* Modal de Confirmação de Exclusão */}
      <M.Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <M.DialogTitle id="alert-dialog-title">{"Confirmar Exclusão"}</M.DialogTitle>
        <M.DialogContent>
          <M.DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir o exame "{exameToDelete?.nome}" (Código: {exameToDelete?.id_exames})?
          </M.DialogContentText>
        </M.DialogContent>
        <M.DialogActions>
          <M.Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </M.Button>
          <M.Button onClick={confirmDeleteExame} color="secondary" autoFocus>
            Excluir
          </M.Button>
        </M.DialogActions>
      </M.Dialog>

      {/* Modal de Edição */}
      <M.Dialog open={openEditDialog} onClose={handleCloseEditDialog} aria-labelledby="form-dialog-title">
        <M.DialogTitle id="form-dialog-title">Editar Exame</M.DialogTitle>
        <M.DialogContent>
          <M.TextField
            autoFocus
            margin="dense"
            id="editCodExame"
            label="Código do Exame"
            type="text"
            fullWidth
            value={editCodExame}
            onChange={(e) => setEditCodExame(e.target.value)}
            disabled
          />
          <M.TextField
            margin="dense"
            id="editNomeExame"
            label="Nome do Exame"
            type="text"
            fullWidth
            value={editNomeExame}
            onChange={(e) => setEditNomeExame(e.target.value)}
          />
        </M.DialogContent>
        <M.DialogActions>
          <M.Button onClick={handleCloseEditDialog} color="primary">
            Cancelar
          </M.Button>
          <M.Button onClick={saveEditedExame} color="primary">
            Salvar
          </M.Button>
        </M.DialogActions>
      </M.Dialog>

      <ToastMessage
        code={messageSend.cod}
        message={messageSend.message}
        onClose={() =>
          setMessageSend({
            cod: messageSend.cod,
            message: messageSend.message,
            severity: messageSend.severity,
            show: false
          })
        }
        show={messageSend.show}
        severity={messageSend.severity}
      />
    </M.Container>
  );
}